// =============================================================================
// SARS tax reference - regex, sanitiser
// =============================================================================
/**
 * Regex for a canonical SARS tax reference number: exactly 10 digits.
 * Shared by an income tax reference and a PAYE employer reference - SARS
 * issues both in the same 10-digit format. Leading zeros are significant
 * (a real reference can start with one or more zeros), so this is a
 * digit-count check, not a numeric range or a `parseInt` round-trip.
 */
export const SARS_TAX_REFERENCE_REGEX = /^\d{10}$/;
/**
 * The separator characters a SARS reference is routinely copied off a printed
 * document with baked in - slashes (`1234/567/89/0`), spaces, hyphens, or
 * dots - shared by every SARS reference sanitiser (tax reference, VAT number)
 * so they strip the same named set rather than drifting apart one regex
 * literal at a time. A permissive strip (also removing letters, say) would
 * silently accept junk like `1234abc567890` whenever the letters happened to
 * leave 10 digits behind, which is worse than rejecting it outright - so this
 * is a named set, never a blanket non-digit strip.
 *
 * Carries the `g` flag, which makes it safe to reuse with `.replace()` (each
 * call resets `lastIndex` internally) but stateful under `.test()` - use it
 * only with `.replace()`.
 */
export const SARS_REFERENCE_SEPARATORS = /[\s/.-]/g;
/**
 * Sanitise a SARS tax reference to its canonical 10-digit form.
 *
 * Returns the canonical 10-digit string, or `null` if the sanitised value is
 * not exactly 10 digits. Leading zeros are preserved: a reference beginning
 * `00` is a real, valid form and must round-trip unchanged - do not "simplify"
 * this with `Number()` or `parseInt`, which would drop them.
 */
export function sanitiseSarsTaxReference(input) {
    const stripped = input.replace(SARS_REFERENCE_SEPARATORS, '');
    return SARS_TAX_REFERENCE_REGEX.test(stripped) ? stripped : null;
}
